import { useState, useRef, useEffect } from 'react';
import { Globe, ChevronDown, ArrowRightCircle, Loader2, Lock } from 'lucide-react';
import { getEnvironments, getEnvironmentByApiUrl } from '../lib/environments';
import type { Environment } from '../lib/environments';

interface ServerSwitcherProps {
  currentUrl: string;
  currentEnvKey: string | null;
  onSwitch: (url: string) => void;
  onReauth: (envConfig: Environment) => void;
  loading: boolean;
}

export const ServerSwitcher = ({ currentUrl, currentEnvKey, onSwitch, onReauth, loading }: ServerSwitcherProps) => {
  const [url, setUrl] = useState(currentUrl || '');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const environments = getEnvironments();

  useEffect(() => {
    if (currentUrl) setUrl(currentUrl);
  }, [currentUrl]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isDirty = url.replace(/\/+$/, '') !== (currentUrl || '').replace(/\/+$/, '');

  /**
   * Determine if switching to a given URL requires re-authentication.
   * Returns the target env config if re-auth is needed, null otherwise.
   */
  const needsReauth = (targetUrl: string): Environment | null => {
    const targetEnv = getEnvironmentByApiUrl(targetUrl);
    if (!targetEnv) return null;
    if (targetEnv.key === currentEnvKey) return null;
    if (!targetEnv.cognito.domain || !targetEnv.cognito.clientId) return null;
    return targetEnv;
  };

  const handleSwitch = () => {
    if (!url || loading) return;
    const clean = url.replace(/\/+$/, '');
    const targetEnv = needsReauth(clean);
    if (targetEnv) {
      onReauth(targetEnv);
    } else {
      onSwitch(clean);
    }
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSwitch();
  };

  const selectPreset = (env: Environment) => {
    setUrl(env.apiUrl);
    setOpen(false);
    const clean = env.apiUrl.replace(/\/+$/, '');
    if (clean === (currentUrl || '').replace(/\/+$/, '')) return;

    if (env.key !== currentEnvKey && env.cognito.domain && env.cognito.clientId) {
      onReauth(env);
    } else {
      onSwitch(clean);
    }
  };

  const pendingReauth = isDirty ? needsReauth(url.replace(/\/+$/, '')) : null;

  return (
    <div ref={ref} className="relative flex items-center gap-1.5">
      <Globe size={14} className="text-gray-500 flex-shrink-0" />

      <input
        type="url"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={loading}
        className="bg-gray-900 border border-gray-600 rounded-l px-2 py-1 text-xs text-gray-200 w-56
                   focus:outline-none focus:border-blue-500 disabled:opacity-50 placeholder-gray-500"
        placeholder="Server URL"
        title="API server URL"
      />

      <button
        onClick={() => setOpen(!open)}
        disabled={loading}
        className="bg-gray-900 border border-l-0 border-gray-600 rounded-r px-1.5 py-1 text-gray-400
                   hover:text-white hover:bg-gray-700 disabled:opacity-50"
        title="Select environment"
      >
        <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {isDirty && (
        <button
          onClick={handleSwitch}
          disabled={loading || !url}
          className={`flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-white ml-1
            ${pendingReauth
              ? 'bg-amber-600 hover:bg-amber-700'
              : 'bg-blue-600 hover:bg-blue-700'
            } disabled:bg-gray-600`}
          title={pendingReauth ? `Re-authenticate with ${pendingReauth.label}` : 'Switch server'}
        >
          {loading ? (
            <Loader2 size={12} className="animate-spin" />
          ) : pendingReauth ? (
            <Lock size={12} />
          ) : (
            <ArrowRightCircle size={12} />
          )}
          {pendingReauth ? 'Re-auth & Switch' : 'Switch'}
        </button>
      )}

      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-gray-800 border border-gray-600 rounded shadow-lg min-w-72">
          {environments.map((env) => {
            const isActive = env.apiUrl.replace(/\/+$/, '') === (currentUrl || '').replace(/\/+$/, '');
            const isSameAuth = env.key === currentEnvKey;
            const hasAuth = Boolean(env.cognito.domain && env.cognito.clientId);

            return (
              <button
                key={env.key}
                onClick={() => selectPreset(env)}
                className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-700 flex items-center justify-between gap-2
                  ${isActive ? 'text-blue-400' : 'text-gray-300'}`}
              >
                <div className="min-w-0">
                  <div className="font-medium flex items-center gap-1.5">
                    {env.label}
                    {!isSameAuth && hasAuth && !isActive && (
                      <span title="Requires re-authentication"><Lock size={10} className="text-amber-400" /></span>
                    )}
                  </div>
                  <div className="text-gray-500 font-mono truncate">{env.apiUrl}</div>
                </div>
                {isActive && (
                  <span className="text-[10px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded flex-shrink-0">
                    active
                  </span>
                )}
              </button>
            );
          })}

          {environments.length === 0 && (
            <div className="px-3 py-2 text-xs text-gray-500">
              No environments configured. Type a URL above.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
